import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, ChangeEvent, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useAppSelector } from '../../../app/hooks'
import KeycloakService from '../../../utils/keycloakService'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { selectLocations } from '../../location/locationSlice'
import {
  BTN_CANCEL,
  BTN_CREATE,
  BTN_UPDATE,
  CREATE_HOSPITAL,
  EDIT_HOSPITAL,
  FieldConfig,
  FIELD_ROWS,
  HOSPITAL_FIELD_LOCATION,
  HOSPITAL_FIELD_PHONE,
  HOSPITAL_FIELD_PINCODE,
  hospitalValidationSchema,
  HospitalForm,
  NUMERIC_INPUT_PATTERN,
  NUMERIC_ONLY_SANITIZE_REGEX,
  PHONE_INPUT_MAX_LENGTH,
  PINCODE_INPUT_MAX_LENGTH,
  TOAST_HOSPITAL_CREATE_SUCCESS,
  TOAST_HOSPITAL_DUPLICATE,
  TOAST_HOSPITAL_FAIL,
  TOAST_HOSPITAL_UPDATE_SUCCESS,
  UNKNOWN_USER,
} from '../constants'
import {
  checkHospitalDuplicate,
  createHospital,
  updateHospital,
} from '../../../services/referralService'

const styles = () => ({
  hospitalField: {
    '& .MuiInputBase-input': { fontSize: '14px', padding: '8px' },
    '& .MuiInputLabel-root': { fontSize: '14px' },
    '& .MuiFormLabel-asterisk': { color: '#d32f2f' },
  },
  dialogTitle: {
    fontSize: '14px',
    fontWeight: 600,
  },
  dialogContent: {
    padding: '10px !important',
  },
})

export interface HospitalDialogProps {
  open: boolean
  onClose: () => void
  hospital?: any
}

const HospitalDialog = ({ open, onClose, hospital }: HospitalDialogProps) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const locations = useAppSelector(selectLocations)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

  const locationOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (locations ?? [])
            ?.map((loc) => loc?.resource?.name?.trim())
            ?.filter((name): name is string => Boolean(name))
        )
      ),
    [locations]
  )

  const handleCreate = async (values: HospitalForm) => {
    const duplicate = await checkHospitalDuplicate(
      values?.name,
      values?.location,
      values?.type
    )
    if (duplicate) {
      toast.error(TOAST_HOSPITAL_DUPLICATE)
      return
    }
    const payload = {
      name: values?.name?.trim(),
      description: values?.description?.trim(),
      location: values?.location?.trim(),
      street: values?.street?.trim(),
      city: values?.city?.trim(),
      state: values?.state?.trim(),
      pincode: values?.pincode?.trim(),
      phone: values?.phone?.trim(),
      email: values?.email?.trim(),
      type: values?.type?.trim(),
      created_by: KeycloakService?.getUsername() || UNKNOWN_USER,
      created_on: new Date().toISOString(),
    }
    await createHospital(payload)
    toast.success(TOAST_HOSPITAL_CREATE_SUCCESS)
  }

  const handleUpdate = async (values: HospitalForm) => {
    const duplicate = await checkHospitalDuplicate(
      values?.name,
      values?.location,
      values?.type,
      hospital?.id
    )
    if (duplicate) {
      toast.error(TOAST_HOSPITAL_DUPLICATE)
      return
    }
    const payload = {
      id: hospital?.id,
      name: values?.name?.trim(),
      description: values?.description?.trim(),
      location: values?.location?.trim(),
      street: values?.street?.trim(),
      city: values?.city?.trim(),
      state: values?.state?.trim(),
      pincode: values?.pincode?.trim(),
      phone: values?.phone?.trim(),
      email: values?.email?.trim(),
      type: values?.type?.trim(),
      updated_by: KeycloakService?.getUsername() || UNKNOWN_USER,
      updated_on: new Date().toISOString(),
    }
    await updateHospital(hospital?.id, payload)
    toast.success(TOAST_HOSPITAL_UPDATE_SUCCESS)
  }

  const handleSubmit = async (values: HospitalForm) => {
    setIsSubmitDisabled(true)
    try {
      if (hospital?.id) {
        await handleUpdate(values)
      } else {
        await handleCreate(values)
      }
      onClose()
    } catch {
      toast.error(TOAST_HOSPITAL_FAIL(hospital?.id ? 'update' : 'create'))
    } finally {
      setIsSubmitDisabled(false)
    }
  }

  const formik = useFormik<HospitalForm>({
    initialValues: {
      name: hospital?.name?.trim() ?? '',
      description: hospital?.description?.trim() ?? '',
      location: hospital?.location?.trim() ?? '',
      street: hospital?.street?.trim() ?? '',
      city: hospital?.city?.trim() ?? '',
      state: hospital?.state?.trim() ?? '',
      pincode: hospital?.pincode?.trim() ?? '',
      phone: hospital?.phone?.trim() ?? '',
      email: hospital?.email?.trim() ?? '',
      type: hospital?.type?.trim() ?? '',
    },
    enableReinitialize: true,
    validationSchema: hospitalValidationSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    if (!open) formik.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleFieldChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    if (name === HOSPITAL_FIELD_PHONE || name === HOSPITAL_FIELD_PINCODE) {
      formik.setFieldValue(name, value.replace(NUMERIC_ONLY_SANITIZE_REGEX, ''))
      return
    }
    formik.handleChange(event)
  }

  const renderField = (field: FieldConfig) => {
    const isAutocompleteField =
      field?.isAutocomplete || field?.name === HOSPITAL_FIELD_LOCATION
    const options =
      field?.name === HOSPITAL_FIELD_LOCATION
        ? locationOptions
        : field?.options ?? []

    if (isAutocompleteField) {
      return (
        <Autocomplete
          key={field?.name}
          freeSolo
          fullWidth
          options={options}
          value={formik?.values[field?.name]}
          onChange={(_, value) => {
            formik?.setFieldValue(field?.name, value ?? '')
          }}
          onInputChange={(_, inputValue) => {
            formik?.setFieldValue(field?.name, inputValue)
          }}
          onBlur={() => formik?.setFieldTouched(field?.name, true)}
          renderInput={(params) => (
            <TextField
              {...params}
              name={field?.name}
              label={field?.label}
              required={field?.required}
              size='small'
              error={
                formik?.touched[field?.name] && !!formik?.errors[field?.name]
              }
              helperText={
                formik?.touched[field?.name] ? formik?.errors[field?.name] : ''
              }
              className={classes?.hospitalField}
            />
          )}
        />
      )
    }

    return (
      <TextField
        key={field?.name}
        name={field?.name}
        label={field?.label}
        required={field?.required}
        size='small'
        fullWidth
        multiline={field?.multiline}
        minRows={field?.minRows}
        value={formik?.values[field?.name]}
        onChange={handleFieldChange}
        onBlur={formik?.handleBlur}
        inputProps={
          field?.name === HOSPITAL_FIELD_PHONE
            ? {
                inputMode: 'numeric',
                pattern: NUMERIC_INPUT_PATTERN,
                maxLength: PHONE_INPUT_MAX_LENGTH,
              }
            : field?.name === HOSPITAL_FIELD_PINCODE
            ? {
                inputMode: 'numeric',
                pattern: NUMERIC_INPUT_PATTERN,
                maxLength: PINCODE_INPUT_MAX_LENGTH,
              }
            : undefined
        }
        error={formik?.touched[field?.name] && !!formik?.errors[field?.name]}
        helperText={
          formik?.touched[field?.name] ? formik?.errors[field?.name] : ''
        }
        className={classes?.hospitalField}
      />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle className={classes?.dialogTitle}>
        {hospital ? EDIT_HOSPITAL : CREATE_HOSPITAL}
      </DialogTitle>
      <DialogContent className={classes?.dialogContent}>
        <Box display='flex' flexDirection='column' gap={2} pt={1}>
          {FIELD_ROWS?.map((row, i) =>
            Array.isArray(row) ? (
              <Box key={i} display='grid' gridTemplateColumns='1fr 1fr' gap={2}>
                {row?.map(renderField)}
              </Box>
            ) : (
              renderField(row)
            )
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size='small' onClick={onClose}>
          {BTN_CANCEL}
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={() => formik?.handleSubmit()}
          disabled={isSubmitDisabled || (hospital ? !formik?.dirty : false)}
        >
          {hospital ? BTN_UPDATE : BTN_CREATE}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HospitalDialog
